/*@flow*/
import React, { useState, useEffect, createRef } from 'react';
import * as t from 'lib/t';
import compose from 'lib/compose';
import autosize from 'autosize';
import scrollDrawerTop from 'lib/scrollDrawerTop';
import getBbox from 'lib/getBbox';
import { camelizeKeys } from 'humps';
import nested from 'lib/nested';
import { Fields } from 'lib/proforma';
import Errors from 'comps/Errors';
import { useMutation } from '@apollo/react-hooks';

const validate = (formRef) =>
  new Promise((res, rej) => {
    const form = formRef.current;
    if (!form) {
      rej([{ message: 'An unexpected error occurred.', path: [] }]);
    } else {
      const { value, errors } = form.validate();
      if (errors && errors.length > 0) {
        rej(errors);
      } else if (value) {
        res(value);
      }
    }
  });

type PropsT = {
  value: Object,
  mutation: Object,
  refetchQueries?: Object[],
  variableTransformer?: Function,
  afterSuccess?: Function,
  queryName: string,
  namespace: string,
  onChange: Function,
  onClose: Function,
  onSave: Function,
  schema: Object,
};

const Component = (props: PropsT) => {
  const [isSaving, setSaving] = useState(false);
  const [showAfterSuccess, setShowAfterSuccess] = useState(false);
  const [errors, setErrors] = useState([]);

  const variableTransformer =
    props.variableTransformer || ((input) => ({ input }));

  useEffect(() => {
    const textareas = document.querySelectorAll('textarea');
    autosize(textareas);
    textareas.forEach((v) => {
      $(v).trigger('change');
    });
  }, []);

  const defaults = {
    parameters: ['the_geom'],
    bbox: getBbox(),
  };

  const valueWithDefaults = t.mixin(defaults, props.value, true);
  const containerRef = createRef();
  const formRef = createRef();

  const scrollTop = () => {
    containerRef.current && scrollDrawerTop(containerRef.current);
  };

  const handleSubmit = () => {
    setSaving(true);
    validate(formRef)
      .then(submitPanel)
      .catch(catchValidationError);
  };

  const [mutate] = useMutation(props.mutation);

  const submitPanel = (v) => {
    // Transform the payload using `variableTransformer`
    let variables = v[props.namespace];
    const prepare = compose(camelizeKeys);
    variables = prepare(variables);
    variables = variableTransformer(variables);

    let opts = { variables };
    if (props.refetchQueries) {
      opts = { ...opts, refetchQueries: props.refetchQueries };
    }
    mutate(opts)
      .then(handleMutationSuccess)
      .catch(handleMutationException);
  };

  const getQueryResult = (res) => nested(res, ['data', props.queryName]);

  const handleMutationSuccess = (res) => {
    setSaving(false);

    const payload = getQueryResult(res);
    if (payload.success) {
      // Perform cleanup (errors, etc.) if save succeeds
      setErrors([]);
      if (props.afterSuccess) {
        setShowAfterSuccess(true);
      } else {
        // Close modal
        props.onClose();
        // Handle save
        props.onSave();
      }
    } else {
      // Go up
      scrollTop();
      // Add errors to form state.
      const buildError = (o) =>
        o.errors && o.errors.map((e) => ({ message: e, path: ['Server'] }));
      const errors2 = buildError(payload) || ['Malformed payload object.'];
      setErrors(errors2);
    }
  };

  const handleMutationException = (e) => {
    // Go up
    scrollTop();
    let errors2 = [];

    // Handle execution error.
    if (e.message) {
      errors2 = [e];
    } else if (e.errors) {
      errors2 = e.errors;
    } else {
      // Handle type error.
      const payload = getQueryResult(e);
      errors2 = nested(payload, 'errors', ['Unhandled exception.']);
    }

    setErrors(errors2);
    setSaving(false);
  };

  const catchValidationError = (e) => {
    scrollTop();
    let errors2 = [e];
    if (Array.isArray(e)) {
      errors2 = e;
    }
    // Save errors locally.
    setErrors(errors2);
    setSaving(false);
  };

  if (props.afterSuccess && showAfterSuccess) {
    return props.afterSuccess(props);
  }

  const value = { [props.namespace]: valueWithDefaults };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      ref={containerRef}
    >
      <Errors errors={errors} />
      <div
        style={{
          display: 'grid',
          gridTemplateRows: '1fr 80px',
          height: '100%',
        }}
      >
        <Fields
          ref={formRef}
          value={value}
          type={props.schema}
          onChange={(v) => {
            props.onChange(v[props.namespace]);
          }}
        />
        <footer
          style={{
            borderTop: 'solid 1px #efefef',
          }}
          className="pt-3 mt-3"
        >
          <button
            onClick={handleSubmit}
            className="btn btn-primary btn-lg"
            disabled={isSaving}
            data-testid="SaveBtn"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </footer>
      </div>
    </form>
  );
};
export default Component;
